import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { Configuration, InsightConfigurationType, Role } from 'src/app/core/models/insight-configuration.model';
import { GetConfigurationList, GetConfigurationTypes, GetPlantList, GetRolesAndDepartmentByAsset, SetSelectedConfigType, SetSelectedPlant } from 'src/app/core/store/insight-configuration.action';
import { GetEditKPIConfiguration, SetKPIMode } from 'src/app/core/store/kpi-configuration/kpi-configuration.action';
import { GetPredictiveConfigurationDetails, SetPredictiveFailureMode } from 'src/app/core/store/predictive-failure-configuration/predictive-failure-configuration.action';
import { CONFIG_TYPE, CONFIG_TYPE_URL, CONFIG_MODE } from 'src/app/core/enums/insightConfigurations.enum';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { DeleteConfigurationPopupComponent } from './delete-configuration-popup/delete-configuration-popup.component';
import { GetUoMDependencies } from 'src/app/core/store/unit-of-measure/unit-of-measure.action';
import {InsightConfigurationService} from 'src/app/core/services/insight-configuration.service';
import { GetEwbConfigurationDetails, SetEwbFailureMode, SetEwbMode } from 'src/app/core/store/ewb-configuration/ewb-configuration.action';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator, {static : true}) paginator: MatPaginator;
   
  public configurationTypes : InsightConfigurationType[];

  public userHasAccess = this.store.selectSnapshot(state => state.configurations.userHasAccess);

  public dataSource = new MatTableDataSource();
  public displayedColumns: string[] = ['category', 'type', 'theme', 'subtype', 'template', 'roleName', 'departmentName', 'actionIn', 'createdBy', 'createdDate', 'option'];
 
  public configurationList: Configuration[] = [];
  public limit = 8;
  public isLoading = false;
  public url : string;
  public unsubscribe  = new Subject<void>();
  public createdDate: string;
  public screenLoading = false;
  public plantOptions: any[] = [];
  public showPlantDropdown: boolean = false;
  public selectedPlantName: string = '';
  public multiPlantSelected: boolean = false;
  public deleteConfigurationModalRef: BsModalRef;
  public roleIdsByPlant: any[];
  public roleListByPlant: Role[] = [];
  public path: string;

  public firstLoad = true;

  constructor(
    public router: Router,
    public store: Store,
    public modalService: BsModalService,
    private cdr: ChangeDetectorRef,
    public InsightConfigurationService:InsightConfigurationService
  ) {}

  showDropdown: boolean = false;
  showOptions: boolean = false;

  ngOnInit(): void {
    this.isLoading = true;
    this.getUoMRelatedDependencies();
    this.getConfigurationTypes();
    this.getPlantOptions();
  }

  ngAfterViewInit() {
    this.getConfigurationList();
    this.cdr.detectChanges();
  }
  public getConfigurationTypes() {
    this.store.dispatch(new GetConfigurationTypes())
    .subscribe((state) => {
      this.configurationTypes = state.configurations.configurationTypes;
    })
  }

  public retrieveConfigurationsList(showLoading: boolean = false) {
    let storedPlant = this.store.selectSnapshot(state => state.configurations.selectedPlant);
    this.isLoading = showLoading;
    this.selectedPlantName  = storedPlant?.plantName;
    this.store.dispatch(new GetConfigurationList(storedPlant?.plantId))
  }

  public getConfigurationList() {
    
    this.store.select(state => state.configurations.insightsConfigurationsList).pipe(takeUntil(this.unsubscribe))
    .subscribe((list) => {
      const plantsLoaded = this.store.selectSnapshot(state => state.configurations.plantsLoaded);

      if(plantsLoaded) {
        this.isLoading= false;
        this.configurationList  = list;

        if(this.firstLoad) {
          this.dataSource = new MatTableDataSource(this.configurationList);
          this.dataSource.paginator = this.paginator;
          this.paginator._intl.nextPageLabel ='';
          this.paginator._intl.previousPageLabel ='';
          this.paginator._intl.firstPageLabel = '';
          this.paginator._intl.lastPageLabel = '';
          this.firstLoad = false;
        } else {
          this.dataSource.data = this.configurationList;
        }
      }
    })
  }

  public showDropdownMenu(): void{
    this.showDropdown = !this.showDropdown;
  }

  public navigateToConfig(configurationType: InsightConfigurationType): void {
    const viewId = configurationType.viewId;
    let url = '';
    let selectedCategory = configurationType.category;
    this.path = selectedCategory.replace(/\s+/g, '-').toLowerCase();

    if(viewId === CONFIG_TYPE.KPI) {
      url = CONFIG_TYPE_URL.KPI + this.path;
      this.store.dispatch(new SetKPIMode(CONFIG_MODE.CREATE))
    } else if (viewId === CONFIG_TYPE.PREDICTIVE_FAILURE || viewId === CONFIG_TYPE.CUSTOM_PREDICTIVE || viewId === CONFIG_TYPE.ML_ANOMALY_DETECTION) {
      url = CONFIG_TYPE_URL.PREDICTIVE_FAILURE + this.path;
      this.store.dispatch(new SetPredictiveFailureMode(CONFIG_MODE.CREATE));
    }
    else if (viewId === CONFIG_TYPE.EWB_CONFIGURATION){
      url = CONFIG_TYPE_URL.EWB_CONFIGURATION + this.path;
      this.store.dispatch(new SetEwbFailureMode(CONFIG_MODE.CREATE));
    }
    this.store.dispatch(new SetSelectedConfigType(configurationType));
    this.router.navigateByUrl(url);
  }

 public onClickedOutside(): void {
    this.showDropdown = false;
  }

  public editConfiguration(configuration: Configuration){
    let url = '';
    this.screenLoading = true;
    const uid = configuration.uid;
    const categoryId = configuration.categoryId;
    let selectedCategory = configuration.category;
    this.path = selectedCategory.replace(/\s+/g, '-').toLowerCase();
    
    if(categoryId === CONFIG_TYPE.KPI) {
      url = CONFIG_TYPE_URL.KPI + this.path;
      this.store.dispatch(new SetKPIMode(CONFIG_MODE.EDIT));
      this.store.dispatch(new GetEditKPIConfiguration(uid)).pipe(takeUntil(this.unsubscribe)).subscribe(
        () => {this.router.navigateByUrl(url);},
        () => {this.isLoading = false;}
      )
    } else if(categoryId === CONFIG_TYPE.PREDICTIVE_FAILURE || categoryId === CONFIG_TYPE.CUSTOM_PREDICTIVE || categoryId === CONFIG_TYPE.ML_ANOMALY_DETECTION) {
      url = CONFIG_TYPE_URL.PREDICTIVE_FAILURE + this.path;
      this.store.dispatch(new SetPredictiveFailureMode(CONFIG_MODE.EDIT));
      this.store.dispatch(new GetPredictiveConfigurationDetails(uid))
      .pipe(
        takeUntil(this.unsubscribe),
        catchError(() => {
          this.screenLoading = false;
          return of()
        }),
      ).subscribe(() => {
        this.screenLoading = false;
        this.router.navigateByUrl(url)
      })
    }
    else if(categoryId === CONFIG_TYPE.EWB_CONFIGURATION ){
       url = CONFIG_TYPE_URL.EWB_CONFIGURATION + this.path;
        this.store.dispatch(new SetEwbMode(CONFIG_MODE.EDIT));
      this.store.dispatch(new GetEwbConfigurationDetails(uid))
      .pipe(
        takeUntil(this.unsubscribe),
        catchError(() => {
          this.screenLoading = false;
          return of()
        }),
      ).subscribe(() => {
        this.screenLoading = false;
        this.router.navigateByUrl(url)
      })
    }
  }

  public deleteConfiguration(configuration: Configuration){
    this.deleteConfigurationModalRef = this.modalService.show(DeleteConfigurationPopupComponent, {
      initialState: {
        configuration: configuration
      },
      class: 'modal-message-lg delete-message-modal-container',
      ignoreBackdropClick: true
    })
    this.deleteConfigurationModalRef?.content.confirm.subscribe(confirmation => {
      if(confirmation === true){
        this.dataSource.data.splice(this.dataSource.data.indexOf(configuration),1);
        this.dataSource = new MatTableDataSource(this.dataSource.data);
        this.dataSource.paginator = this.paginator;
        this.paginator._intl.nextPageLabel ='';
        this.paginator._intl.previousPageLabel ='';
        this.paginator._intl.firstPageLabel = '';
        this.paginator._intl.lastPageLabel = '';

        this.retrieveConfigurationsList(false)
      }
    })
  }

  public getPlantOptions(){
    let storedPlant = this.store.selectSnapshot(state => state.configurations.selectedPlant);
    let plantsLoaded = this.store.selectSnapshot(state => state.configurations.plantsLoaded);

    //Listen to list of plants update in state
    this.store.select(state => state.configurations.plantList).pipe(takeUntil(this.unsubscribe))
    .subscribe((plants) => {
      this.plantOptions = plants;

      if(this.plantOptions[0]?.plantId && this.plantOptions[0]?.plantId != '' && !plantsLoaded) {
        // intiial Load
        this.selectPlantOption({
          option: this.plantOptions[0]
        });
      } else if(storedPlant != null) {
          this.selectedPlantName  = storedPlant.plantName;
          this.store.dispatch(new GetConfigurationList(storedPlant.plantId))
      }
    })

    if(!plantsLoaded) {
      this.store.dispatch(new GetPlantList())
    } 
    
  }

  public selectPlants() {
    //to expand/collapse the select Plant dropdown
    this.showPlantDropdown = !this.showPlantDropdown;
  }

  public onClickedOutsidePlants(): void {
    //to collapse the select Plant dropdown
    this.showPlantDropdown = false;
  }

  public selectPlantOption(plant) {
    //for showing the selected Plant name in input field
    this.selectedPlantName = plant.option?.plantName

    //to store the selected Plant option
    this.store.dispatch(new SetSelectedPlant(plant.option))

    // trigger get roles by plant
    this.getRolesByPlant(plant.option?.plantId)
    
    this.showPlantDropdown = false;
    this.isLoading = true;
  }

  public getRolesByPlant(assetId: string) {

    this.store.dispatch(new GetRolesAndDepartmentByAsset(assetId));
  }

  public getUoMRelatedDependencies() {
    let loaded = this.store.selectSnapshot(state => state.unitOfMeasure.loadedUoMDependencies)
    if(!loaded) {
      this.store.dispatch(new GetUoMDependencies())
    }
  }
  
}
